/**
 * Eine Kassensession, aufgeklappt.
 *
 * Von oben nach unten, wie man einen Fall liest:
 *
 * 1. Was passiert ist — oder, wenn alles stimmt, genau das in einem Satz.
 * 2. Die Rechnung, Zeile für Zeile. Korrigiert wird dort, wo der Wert steht:
 *    „ändern“ am Betrag, „eintragen“ an einem fehlenden, „festsetzen“ am
 *    Trinkgeld.
 * 3. Nur bei einem offenen roten Hinweis: die Entscheidung. Höchstens zwei
 *    Knöpfe, beschriftet mit dem, was man feststellt (`sessionAdvice.ts`).
 * 4. Wofür die Kasse zählt, Bereich und Betriebstag, beides änderbar.
 * 5. Ganz unten, zugeklappt, die Belege: zum Nachprüfen, selten gebraucht.
 *
 * Vorher trug jede Session denselben Knopf „Bestätigen“ und ein Menü „Andere
 * Möglichkeiten“ mit vier Aktionen. Im ersten Testlauf mit der Buchhaltung
 * (Oktober 2026) passte beides nicht: was stimmt, muss niemand bestätigen, und
 * wer einen Betrag korrigieren will, sucht ihn am Betrag, nicht in einem Menü.
 *
 * Die Notiz ist freiwillig. Wer, was und wann hält das Audit-Log fest; die Notiz
 * beantwortet nur das Warum und lohnt sich vor allem, wo Werte von Hand kommen.
 */
import { useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, type CashSessionDto, type WorkstationDto } from "../lib/api";
import { adviseSession, type ActionKind } from "../lib/sessionAdvice";
import { euro, longDate, money, shortDate } from "../lib/format";
import { Receipts } from "./Receipts";
import { ErrorBox } from "./ui";

type Feld = "cardTotal" | "zBon";

/** Was gerade bearbeitet wird. Immer nur eines auf einmal. */
type Bearbeitung =
  | { art: "werte"; felder: Feld[] }
  | { art: "fehlt"; feld: Feld }
  | { art: "trinkgeld" }
  | { art: "bereich" }
  | { art: "tag" };

/** Was am Ende an die API geht. */
type Auftrag =
  | { art: "confirm" }
  | { art: "noTip" }
  | { art: "uebertrag" }
  | { art: "werte"; zBon?: string; cardTotal?: string }
  | { art: "fehlt"; feld: Feld; betrag: string }
  | { art: "trinkgeld"; betrag: string }
  | { art: "bereich"; ziel: string | null }
  | { art: "tag"; datum: string };

/** Auswahlwert für „eigene Kasse“; ein leerer Wert sperrte das Pflichtfeld. */
const EIGENE = "__eigene__";

const FELD_TEXT: Record<Feld, string> = {
  cardTotal: "am Karten-Terminal angekommen",
  zBon: "Kartenumsatz laut Kasse",
};

export function SessionPanel({
  session,
  workstations,
  gesperrt = false,
  onDone,
}: {
  session: CashSessionDto;
  workstations: WorkstationDto[];
  /** Ausgezahlter Tag: zeigen, aber nichts mehr anbieten. */
  gesperrt?: boolean;
  onDone?: () => void;
}) {
  const advice = adviseSession(session);
  const geteilt = session.kontext?.geteilterBeleg ?? null;
  const [bearbeitung, setBearbeitung] = useState<Bearbeitung | null>(null);
  const [eingabe, setEingabe] = useState(() => startwerte(session));
  const [notiz, setNotiz] = useState("");
  const [belegeOffen, setBelegeOffen] = useState(false);
  const queryClient = useQueryClient();

  const auftrag = useMutation({
    mutationFn: (a: Auftrag) => sende(session, a, notiz),
    onSuccess: async () => {
      setBearbeitung(null);
      setNotiz("");
      await queryClient.invalidateQueries();
      onDone?.();
    },
  });

  const oeffne = (b: Bearbeitung) => {
    setEingabe(startwerte(session));
    auftrag.reset();
    setBearbeitung(b);
  };
  const schliesse = () => {
    setBearbeitung(null);
    auftrag.reset();
  };
  /** „ändern“ an einer zweiten Zeile nimmt sie in dieselbe Korrektur auf. */
  const aendere = (feld: Feld) =>
    bearbeitung?.art === "werte"
      ? setBearbeitung({ art: "werte", felder: [...new Set([...bearbeitung.felder, feld])] })
      : oeffne({ art: "werte", felder: [feld] });

  /** Ein Knopf: sofort ausführen, oder die Stelle öffnen, an der eingetragen wird. */
  const entscheide = (aktion: ActionKind) => {
    switch (aktion) {
      case "confirm":
        return auftrag.mutate({ art: "confirm" });
      case "noTip":
        return auftrag.mutate({ art: "noTip" });
      case "uebertrag":
        return auftrag.mutate({ art: "uebertrag" });
      case "counterpart":
        return oeffne({ art: "fehlt", feld: session.cardTotal === null ? "cardTotal" : "zBon" });
      case "amounts":
        return oeffne({ art: "werte", felder: ["cardTotal", "zBon"] });
      case "tipOverride":
        return oeffne({ art: "trinkgeld" });
      case "actsAs":
        return oeffne({ art: "bereich" });
      case "date":
        return oeffne({ art: "tag" });
    }
  };

  const speichere = (e: FormEvent) => {
    e.preventDefault();
    if (!bearbeitung) return;
    switch (bearbeitung.art) {
      case "werte":
        return auftrag.mutate({
          art: "werte",
          ...(bearbeitung.felder.includes("zBon") ? { zBon: normalize(eingabe.zBon) } : {}),
          ...(bearbeitung.felder.includes("cardTotal")
            ? { cardTotal: normalize(eingabe.cardTotal) }
            : {}),
        });
      case "fehlt":
        return auftrag.mutate({
          art: "fehlt",
          feld: bearbeitung.feld,
          betrag: normalize(eingabe[bearbeitung.feld]),
        });
      case "trinkgeld":
        return auftrag.mutate({ art: "trinkgeld", betrag: normalize(eingabe.trinkgeld) });
      case "bereich":
        return auftrag.mutate({ art: "bereich", ziel: eingabe.ziel === EIGENE ? null : eingabe.ziel });
      case "tag":
        return auftrag.mutate({ art: "tag", datum: eingabe.tag });
    }
  };

  const fehler = auftrag.error ? <ErrorBox error={auftrag.error} /> : null;
  // Verweise nur, solange nichts anderes offen ist; eine zweite Betragszeile
  // darf zu einer laufenden Korrektur dazukommen.
  const frei = !gesperrt && !auftrag.isPending && bearbeitung === null;
  const rechnungOffen =
    bearbeitung?.art === "werte" || bearbeitung?.art === "fehlt" || bearbeitung?.art === "trinkgeld";

  const betragsZeile = (feld: Feld, minus = false): Zeile => {
    const wert = session[feld];
    const offen =
      (bearbeitung?.art === "werte" && bearbeitung.felder.includes(feld)) ||
      (bearbeitung?.art === "fehlt" && bearbeitung.feld === feld);
    const zweiteZeile = bearbeitung?.art === "werte" && !gesperrt && !auftrag.isPending;
    return {
      betrag: wert,
      text: FELD_TEXT[feld],
      minus,
      fehlt: wert === null,
      eingabe: offen ? (
        <BetragEingabe
          wert={eingabe[feld]}
          onChange={(v) => setEingabe((alt) => ({ ...alt, [feld]: v }))}
          label={FELD_TEXT[feld]}
        />
      ) : undefined,
      verweis: offen
        ? null
        : wert === null
          ? frei
            ? { text: "eintragen", onClick: () => oeffne({ art: "fehlt", feld }) }
            : null
          : frei || zweiteZeile
            ? { text: "ändern", onClick: () => aendere(feld) }
            : null,
    };
  };

  const vollstaendig = session.zBon !== null && session.cardTotal !== null;
  const trinkgeldZeile: Zeile =
    bearbeitung?.art === "trinkgeld"
      ? {
          betrag: null,
          text: "Trinkgeld, von Hand festgesetzt",
          summe: true,
          eingabe: (
            <BetragEingabe
              wert={eingabe.trinkgeld}
              onChange={(v) => setEingabe((alt) => ({ ...alt, trinkgeld: v }))}
              label="Trinkgeld"
            />
          ),
        }
      : rechnungOffen
        ? { betrag: null, text: "Trinkgeld, wird neu gerechnet", summe: true }
        : vollstaendig
          ? {
              betrag: session.tipAmount,
              text: session.tipAmountOverride ? "Trinkgeld, von Hand festgesetzt" : "Trinkgeld",
              summe: true,
              verweis: frei ? { text: "festsetzen", onClick: () => oeffne({ art: "trinkgeld" }) } : null,
            }
          : // Fehlt eine Seite, ist das Trinkgeld nicht 0, sondern unbekannt.
            { betrag: null, text: "Trinkgeld lässt sich so nicht berechnen", summe: true };

  const eigeneRechnung = (
    <Rechnung zeilen={[betragsZeile("cardTotal"), betragsZeile("zBon", true), trinkgeldZeile]} />
  );

  const verschoben = session.actsAsWorkstationId !== null;
  // Umgestellt in der Kassentabelle, nur für diesen Tag.
  const topfAbweichend = session.topf !== null && session.topf.id !== session.topfVorgabe?.id;

  return (
    // Auf schmalen Bildschirmen steht das Panel in einer seitlich scrollenden
    // Tabelle: am linken Rand festhalten und auf Bildschirmbreite umbrechen.
    <div className="sticky left-0 max-w-[calc(100vw-2rem)] space-y-5 px-4 py-4 sm:max-w-none">
      {/* --- 1. Lage ------------------------------------------------------ */}
      {advice.hergang.length > 0 ? (
        <section>
          <h4 className="label mb-2">Was ist passiert</h4>
          <div className="max-w-3xl space-y-2 text-sm leading-relaxed">
            {advice.hergang.map((satz, i) => (
              <p key={i} className={i === 0 ? "text-ink" : "text-muted"}>
                {satz}
              </p>
            ))}
          </div>
        </section>
      ) : null}
      {advice.ruhig ? (
        <div className="max-w-3xl text-sm">
          {/* Grün mit Haken, wenn nichts mehr offen ist; ein gelber Hinweis bleibt grau. */}
          <p className={!advice.leitHinweis || session.status === "CONFIRMED" ? "text-ok" : "text-muted"}>
            {!advice.leitHinweis || session.status === "CONFIRMED" ? "✓ " : ""}
            {advice.ruhig}
          </p>
          {advice.sonst ? <p className="mt-1 text-xs text-muted">{advice.sonst}</p> : null}
        </div>
      ) : null}
      {session.reason ? (
        <p className="max-w-3xl text-xs text-muted">
          <span className="label mr-1">Notiz</span> {session.reason}
        </p>
      ) : null}

      {/* --- 2. Rechnung -------------------------------------------------- */}
      <section>
        <h4 className="label mb-2">Wie die App darauf kommt</h4>
        <form onSubmit={speichere} onKeyDown={(e) => e.key === "Escape" && schliesse()}>
          {geteilt ? (
            <>
              <SplitCalculation session={session} />
              {bearbeitung?.art === "werte" ? (
                <div className="mt-4">
                  <div className="label mb-1 normal-case">
                    Werte für {longDate(session.businessDate)}
                  </div>
                  {eigeneRechnung}
                </div>
              ) : frei ? (
                <div className="-ml-3 mt-2">
                  <Verweis onClick={() => oeffne({ art: "werte", felder: ["cardTotal", "zBon"] })}>
                    Werte dieses Abends ändern
                  </Verweis>
                </div>
              ) : null}
            </>
          ) : (
            eigeneRechnung
          )}
          {/* Nur solange offen: nach dem Abziehen rechnete der Vorschlag sonst
              ein zweites Mal vom schon korrigierten Wert ab. Und nur, wenn er
              passt: ein negatives Ergebnis ist kein Vorschlag. */}
          {(session.uebertraege?.length ?? 0) > 0 &&
          (session.uebertraege ?? []).every((g) => g.passt) &&
          advice.zuEntscheiden &&
          !rechnungOffen ? (
            <UebertragRechnung session={session} />
          ) : null}
          {rechnungOffen ? (
            <Speicherleiste
              hinweis={
                bearbeitung?.art === "fehlt"
                  ? bearbeitung.feld === "cardTotal"
                    ? "Der Betrag dieses Abends aus dem VR-Händler-Report. Fehlte der Kassenschnitt, markiert die App danach den nächsten Report dieser Kasse."
                    : "Der Kartenumsatz laut Kassenabschluss."
                  : bearbeitung?.art === "trinkgeld"
                    ? "Nur, wenn unklar ist, welcher der beiden Werte falsch ist. Das Trinkgeld gilt dann so, unabhängig von der Rechnung."
                    : "Ersetzt die importierten Werte. Die Rohwerte bleiben in den Belegen erhalten."
              }
              platzhalter={
                bearbeitung?.art === "fehlt" && bearbeitung.feld === "cardTotal"
                  ? "Notiz, freiwillig: z. B. Umsatz aus Händler-Report vom 10.09."
                  : "Notiz, freiwillig: woher stammt der Wert?"
              }
              notiz={notiz}
              setNotiz={setNotiz}
              pending={auftrag.isPending}
              onCancel={schliesse}
              fehler={fehler}
            />
          ) : null}
        </form>
      </section>

      {session.uebertragNach ? (
        <p className="max-w-3xl rounded border border-accent/30 bg-accent/5 px-3 py-2 text-sm">
          Hier wurden {euro(session.uebertragNach.betrag)} von Hand erfasst.{" "}
          {session.uebertragNach.folge ? (
            <>
              Fehlte der Kassenschnitt, steckt der Betrag noch im nächsten Wert dieser Kasse, am{" "}
              <Link
                className="text-accent hover:underline"
                to={`/betriebstage/${session.uebertragNach.folge.datum}`}
              >
                {longDate(session.uebertragNach.folge.datum)}
              </Link>
              . Dort ist er zur Prüfung markiert.
            </>
          ) : (
            "Ein späterer Wert dieser Kasse ist noch nicht importiert; sobald er da ist, wird er zur Prüfung markiert."
          )}
        </p>
      ) : null}

      {geteilt && geteilt.beteiligte.length > 1 ? <Aufteilung session={session} /> : null}

      {/* --- 3. Entscheidung, nur wenn etwas offen ist -------------------- */}
      {/* Während eingetragen wird, ist die Entscheidung schon getroffen. */}
      {advice.zuEntscheiden && !gesperrt && bearbeitung === null ? (
        <section className="rounded border border-alert/30 bg-page px-4 py-3">
          <h4 className="label mb-2">Zu entscheiden</h4>
          {advice.warum ? <p className="mb-3 max-w-2xl text-sm">{advice.warum}</p> : null}
          {advice.entscheidungen.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              {advice.entscheidungen.map((e, i) => (
                <button
                  key={e.aktion}
                  type="button"
                  className={i === 0 ? "btn-recommend" : "btn-outline"}
                  disabled={auftrag.isPending}
                  onClick={() => entscheide(e.aktion)}
                >
                  {e.label}
                </button>
              ))}
              <input
                className="field min-w-[14rem] flex-1 sm:max-w-sm"
                placeholder="Notiz, freiwillig"
                aria-label="Notiz, freiwillig"
                value={notiz}
                onChange={(e) => setNotiz(e.target.value)}
              />
            </div>
          ) : null}
          {advice.sonst ? <p className="mt-2 max-w-2xl text-xs text-muted">{advice.sonst}</p> : null}
          {fehler}
        </section>
      ) : null}

      {/* --- 4. Wofür die Kasse zählt ------------------------------------- */}
      <section className="text-sm">
        <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
          <div>
            <span className="label mr-2">Zählt für</span>
            {verschoben ? (
              <>
                <strong className="font-medium">{session.actsAsWorkstationName}</strong>
                <span className="text-muted"> · Gerät {session.workstationName} lief heute dort</span>
              </>
            ) : (
              <strong className="font-medium">{session.workstationName}</strong>
            )}
            <span className="text-muted">
              {session.topf === null ? " · ohne Topf" : ` · Topf ${session.topf.name}`}
              {topfAbweichend
                ? `, an diesem Tag statt ${session.topfVorgabe?.name ?? "ohne Topf"}`
                : ""}
            </span>
            {frei ? <Verweis onClick={() => oeffne({ art: "bereich" })}>anderer Bereich</Verweis> : null}
          </div>
          <div>
            <span className="label mr-2">Betriebstag</span>
            {longDate(session.businessDate)}
            {session.dateChanged ? (
              <span className="text-muted">
                {" "}
                · vorgeschlagen war {shortDate(session.proposedBusinessDate)}
              </span>
            ) : null}
            {frei ? <Verweis onClick={() => oeffne({ art: "tag" })}>anderer Tag</Verweis> : null}
          </div>
        </div>

        {bearbeitung?.art === "bereich" ? (
          <form className="mt-3 max-w-2xl" onSubmit={speichere} onKeyDown={(e) => e.key === "Escape" && schliesse()}>
            <label className="block">
              <span className="label mb-1 block">An diesem Abend lief die Kasse als</span>
              <select
                className="field"
                value={eingabe.ziel}
                onChange={(e) => setEingabe((alt) => ({ ...alt, ziel: e.target.value }))}
                autoFocus
              >
                <option value={EIGENE}>{session.workstationName}, die eigene Kasse</option>
                {workstations
                  .filter(
                    (w) =>
                      w.id !== session.workstationId &&
                      (w.active || w.id === session.actsAsWorkstationId),
                  )
                  .map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                      {w.poolCode && !w.excludedFromPool ? ` · Topf ${w.poolCode}` : " · ohne Topf"}
                      {w.posTerminalId ? ` · Terminal ${w.posTerminalId}` : ""}
                    </option>
                  ))}
              </select>
            </label>
            <Speicherleiste
              hinweis="Das Trinkgeld geht dann in den Topf der gewählten Kasse, nur für diesen Abend. Die Stammdaten bleiben, wie sie sind."
              platzhalter="Notiz, freiwillig: z. B. Biergartengerät im Club"
              notiz={notiz}
              setNotiz={setNotiz}
              pending={auftrag.isPending}
              onCancel={schliesse}
              fehler={fehler}
            />
          </form>
        ) : null}

        {bearbeitung?.art === "tag" ? (
          <form className="mt-3 max-w-2xl" onSubmit={speichere} onKeyDown={(e) => e.key === "Escape" && schliesse()}>
            <label className="block">
              <span className="label mb-1 block">Betriebstag</span>
              <input
                type="date"
                className="field w-48"
                value={eingabe.tag}
                onChange={(e) => setEingabe((alt) => ({ ...alt, tag: e.target.value }))}
                required
                autoFocus
              />
            </label>
            <Speicherleiste
              hinweis={`Vorschlag des Abgleichs: ${longDate(session.proposedBusinessDate)}.`}
              platzhalter="Notiz, freiwillig"
              notiz={notiz}
              setNotiz={setNotiz}
              pending={auftrag.isPending}
              onCancel={schliesse}
              fehler={fehler}
            />
          </form>
        ) : null}

        {gesperrt ? (
          <p className="mt-2 text-xs text-muted">
            Der Tag ist gesperrt, also ausgezahlt. Hier lässt sich nichts mehr ändern.
          </p>
        ) : null}
      </section>

      {/* --- 5. Belege, ganz unten ---------------------------------------- */}
      <div>
        <button
          type="button"
          className="label hover:text-ink"
          onClick={() => setBelegeOffen((v) => !v)}
        >
          {belegeOffen ? "▾" : "▸"} Belege aus den Quelldateien
        </button>
        {belegeOffen ? (
          <div className="mt-2 border-l-2 border-line">
            <Receipts session={session} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Ruft die API für einen Auftrag. Gerechnet wird dort, nicht hier. */
async function sende(session: CashSessionDto, a: Auftrag, notiz: string): Promise<unknown> {
  const id = session.id;
  const reason = notiz.trim() === "" ? undefined : notiz.trim();
  const bestaetige = (grund = reason) => api.post(`/cash-sessions/${id}/confirm`, { reason: grund });

  switch (a.art) {
    case "confirm":
      return bestaetige();
    case "noTip":
      return api.post(`/cash-sessions/${id}/no-tip`, { reason });
    case "uebertrag": {
      const gruppen = session.uebertraege ?? [];
      if (gruppen.length === 0 || gruppen.some((g) => g.vorschlag === null)) {
        throw new Error("Für diesen Übertrag gibt es keinen Vorschlag.");
      }
      // Ohne eigene Notiz steht trotzdem im Audit-Log, warum der Wert sank.
      const daten = [...new Set(gruppen.flatMap((g) => g.von.map((v) => shortDate(v.datum))))].join(", ");
      const grund = reason ?? `Übertrag vom ${daten} abgezogen, dort fehlte der Kassenschnitt`;
      await api.post(`/cash-sessions/${id}/amounts`, {
        ...Object.fromEntries(gruppen.map((g) => [g.feld, g.vorschlag])),
        reason: grund,
      });
      return bestaetige(grund);
    }
    case "werte":
      await api.post(`/cash-sessions/${id}/amounts`, {
        zBon: a.zBon,
        cardTotal: a.cardTotal,
        reason,
      });
      return bestaetige();
    case "fehlt":
      return api.post(`/cash-sessions/${id}/counterpart`, {
        kind: a.feld === "cardTotal" ? "REPORT" : "CLOSING",
        amount: a.betrag,
        reason,
      });
    case "trinkgeld":
      await api.post(`/cash-sessions/${id}/tip-override`, { tipAmount: a.betrag, reason });
      return bestaetige();
    case "bereich":
      await api.patch(`/cash-sessions/${id}`, { actsAsWorkstationId: a.ziel, reason });
      return bestaetige();
    case "tag":
      return api.patch(`/cash-sessions/${id}`, { businessDate: a.datum, reason });
  }
}

/** Eingabewerte, vorbelegt mit dem, was gilt — beim Übertrag mit dem Vorschlag. */
function startwerte(s: CashSessionDto) {
  const vorschlag = (feld: Feld) => s.uebertraege?.find((g) => g.feld === feld)?.vorschlag ?? null;
  return {
    zBon: deutsch(vorschlag("zBon") ?? s.zBon),
    cardTotal: deutsch(vorschlag("cardTotal") ?? s.cardTotal),
    trinkgeld: deutsch(s.tipAmount),
    ziel: s.actsAsWorkstationId ?? EIGENE,
    tag: s.businessDate,
  };
}

/** API-Betrag in die Schreibweise, die man auch eintippen würde. */
function deutsch(wert: string | null): string {
  return wert === null ? "" : money(wert).replace("−", "-");
}

/** Deutsche Eingabe (`1.234,56`) in das API-Format (`1234.56`). */
function normalize(value: string): string {
  const trimmed = value.trim().replace(/\s/g, "").replace("−", "-").replace(/€$/, "");
  if (trimmed.includes(",")) return trimmed.replace(/\./g, "").replace(",", ".");
  return trimmed;
}

// --- Bausteine -------------------------------------------------------------

interface Zeile {
  betrag: string | null;
  text: string;
  minus?: boolean;
  summe?: boolean;
  /** Fehlt der Wert, steht „fehlt“ statt eines Strichs. */
  fehlt?: boolean;
  /** Eingabefeld statt Betrag, solange die Zeile bearbeitet wird. */
  eingabe?: ReactNode;
  /** Am Zeilenende: „ändern“, „eintragen“ oder „festsetzen“. */
  verweis?: { text: string; onClick: () => void } | null;
}

/** Eine Rechnung, wie man sie auf Papier schreiben würde. */
function Rechnung({ zeilen }: { zeilen: Zeile[] }) {
  return (
    <table className="text-sm">
      <tbody>
        {zeilen.map((z, i) => (
          <tr key={i} className={z.summe ? "border-t border-ink/30" : ""}>
            <td className="w-5 pr-1 text-right font-mono text-muted">{z.minus ? "−" : ""}</td>
            <td
              className={`num whitespace-nowrap py-1 pr-4 ${z.summe ? "font-medium text-ink" : ""}`}
            >
              {z.eingabe ??
                (z.betrag === null && z.fehlt ? <span className="text-alert">fehlt</span> : euro(z.betrag))}
            </td>
            <td className={`py-1 ${z.summe ? "text-ink" : "text-muted"}`}>{z.text}</td>
            <td className="py-1 pl-4">
              {z.verweis ? <Verweis onClick={z.verweis.onClick}>{z.verweis.text}</Verweis> : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BetragEingabe({
  wert,
  onChange,
  label,
}: {
  wert: string;
  onChange: (v: string) => void;
  label: string;
}) {
  return (
    <input
      className="field num w-32 py-1 text-right"
      inputMode="decimal"
      value={wert}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      placeholder="0,00"
      required
      autoFocus
    />
  );
}

/** Ein unauffälliger Verweis neben dem, was er ändert. Immer sichtbar, nicht erst beim Überfahren. */
function Verweis({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" className="ml-3 text-xs text-accent hover:underline" onClick={onClick}>
      {children}
    </button>
  );
}

function Speicherleiste({
  hinweis,
  platzhalter,
  notiz,
  setNotiz,
  pending,
  onCancel,
  fehler,
}: {
  hinweis: string | null;
  platzhalter: string;
  notiz: string;
  setNotiz: (v: string) => void;
  pending: boolean;
  onCancel: () => void;
  fehler: ReactNode;
}) {
  return (
    <div className="mt-3 max-w-2xl space-y-2">
      {hinweis ? <p className="text-xs text-muted">{hinweis}</p> : null}
      <div className="flex flex-wrap items-center gap-2">
        <input
          className="field min-w-[16rem] flex-1"
          placeholder={platzhalter}
          aria-label="Notiz, freiwillig"
          value={notiz}
          onChange={(e) => setNotiz(e.target.value)}
        />
        <button type="submit" className="btn-primary" disabled={pending}>
          {pending ? "Speichert" : "Übernehmen"}
        </button>
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
      {fehler}
    </div>
  );
}

/**
 * Folge eines vergessenen Kassenschnitts: der Vorschlag, als Abzug geschrieben.
 * Eine Zeile je früherem Abend — zweimal vergessen heißt zweimal abziehen.
 */
function UebertragRechnung({ session }: { session: CashSessionDto }) {
  const gruppen = session.uebertraege ?? [];
  return (
    <>
      {gruppen.map((g) => {
        const terminal = g.feld === "cardTotal";
        return (
          <div key={g.feld} className="mt-4">
            <div className="label mb-1 normal-case">
              Vorschlag ohne {g.von.length === 1 ? "den Übertrag" : "die Überträge"}
            </div>
            <Rechnung
              zeilen={[
                {
                  betrag: terminal ? session.cardTotal : session.zBon,
                  text: terminal ? "am Karten-Terminal angekommen" : "Kartenumsatz laut Kasse",
                },
                ...g.von.map((v) => ({
                  betrag: v.betrag,
                  text: `davon vom ${longDate(v.datum)}, dort von Hand erfasst`,
                  minus: true,
                })),
                { betrag: g.vorschlag, text: "bleibt für diesen Abend", summe: true },
              ]}
            />
          </div>
        );
      })}
    </>
  );
}

/**
 * Der aufgeteilte Fall. Als Subtraktion geschrieben, so wie man sie auf ein
 * Blatt Papier schreiben würde: was ankam, minus was die Kassen gemeldet haben.
 */
function SplitCalculation({ session }: { session: CashSessionDto }) {
  const geteilt = session.kontext?.geteilterBeleg;
  if (!geteilt) return null;

  const zeilen: Zeile[] =
    geteilt.art === "REPORT"
      ? [
          { betrag: geteilt.betrag, text: "am Karten-Terminal angekommen" },
          ...geteilt.beteiligte.map((b) => ({
            betrag: b.zBon,
            text: `Kassenumsatz ${longDate(b.businessDate)}`,
            minus: true,
          })),
        ]
      : [
          ...geteilt.beteiligte.map((b) => ({
            betrag: b.cardTotal,
            text: `am Karten-Terminal ${longDate(b.businessDate)}`,
          })),
          { betrag: geteilt.betrag, text: "Kassenumsatz laut Abschluss", minus: true },
        ];

  return (
    <>
      <Rechnung
        zeilen={[
          ...zeilen,
          {
            betrag: geteilt.trinkgeldSumme,
            text: `Trinkgeld für ${geteilt.beteiligte.length === 2 ? "beide" : "alle"} Abende zusammen`,
            summe: true,
          },
        ]}
      />
      <p className="mt-2 max-w-2xl text-xs text-muted">
        <strong className="font-medium text-ink">
          Diese {euro(geteilt.trinkgeldSumme)} stehen fest.
        </strong>{" "}
        Sie werden auf jeden Fall ausgezahlt. Unsicher ist nur, welcher Abend welchen Teil
        davon bekommt. Davon hängt ab, welche Leute ihn bekommen.
      </p>
    </>
  );
}

function Aufteilung({ session }: { session: CashSessionDto }) {
  const geteilt = session.kontext?.geteilterBeleg;
  if (!geteilt) return null;
  return (
    <section>
      <h4 className="label mb-1">Die Aufteilung</h4>
      <p className="mb-2 text-xs text-muted">
        Geschätzt im Verhältnis der Umsätze. Diese Zahlen stehen auf keinem Ausdruck.
      </p>
      <table className="w-full max-w-2xl text-sm">
        <thead>
          <tr className="text-muted">
            <th className="py-1 text-left font-normal">Betriebstag</th>
            <th className="py-1 text-right font-normal">Anteil</th>
            <th className="py-1 text-right font-normal">davon Umsatz</th>
            <th className="py-1 text-right font-normal">davon Trinkgeld</th>
          </tr>
        </thead>
        <tbody>
          {geteilt.beteiligte.map((b) => (
            <tr
              key={b.sessionId}
              className={b.istDiese ? "border-t border-line bg-accent/5" : "border-t border-line"}
            >
              <td className="py-1.5">
                {longDate(b.businessDate)}
                {b.istDiese ? <span className="ml-2 text-xs text-accent">dieser Eintrag</span> : null}
              </td>
              <td className="num py-1.5 text-muted">
                {b.anteilProzent ? `${b.anteilProzent.replace(".", ",")} %` : "–"}
              </td>
              <td className="num py-1.5">{euro(b.zBon)}</td>
              <td className="num py-1.5">{euro(b.tipAmount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
